import type { AxiosResponse } from 'axios';
import Repository from './repository';

const resource = '/billing';

type CurrencyCode = 'EUR' | 'USD' | 'GBP' | 'TRY' | string;
type BillingActivityView = 'credit_activity' | 'usage_history' | 'all';
type CreditDirection = 'credit' | 'debit' | string;
type CreditTopUpType =
	| 'bank_transfer'
	| 'trial'
	| 'promo'
	| 'manual_adjustment'
	| string;

export interface PackageMetricItemSummary {
	metricCode: string;
	available: boolean;
	unitPrice?: string | null;
	followPpu?: boolean;
}

export interface BillingPackageTemplateSummary {
	id: string;
	packageCode: string;
	displayName: string;
	currency: string;
	status: string;
	effectiveFrom: string;
	effectiveTo?: string | null;
	monthlyMinimum?: string | null;
	annualCommitment?: string | null;
	annualPaidAmount?: string | null;
	metricItems: PackageMetricItemSummary[];
}

export interface PackageAssignmentSummary {
	id: string;
	packageCode: string;
	assignedAt: string;
	assignedBy: string;
	reason?: string;
	currency: string;
}

export interface VatValidationItem {
	status: string;
	checkedAt?: string | null;
	reason?: string;
}

export interface MerchantBillableMetricsResponse {
	newCase?: string | null;
	newSession?: string | null;
	newSessionBySms?: string | null;
	newAmlSearch?: string | null;
	renewedAmlSearch?: string | null;
	formSubmission?: string | null;
	verifiedId?: string | null;
	addressVerification?: string | null;
	issuerLegitimacy?: string | null;
	documentFraudAnalysis?: string | null;
	livenessCheck?: string | null;
	smsVerified?: string | null;
}

export interface MerchantBillingConfigItem {
	id: string;
	merchantId: string;
	billingType: string;
	status: string;
	currency: string;
	paymentMethod: string;
	managedServices: boolean;
	creditExpiryDate?: string | null;
	billableMetrics: MerchantBillableMetricsResponse;
	businessName?: string;
	addressLine1?: string;
	addressLine2?: string;
	city?: string;
	state?: string;
	postalCode?: string;
	country?: string;
	vatNumber?: string;
	billingEmail?: string;
	closeRequestedAt?: string | null;
	vatValidation?: VatValidationItem | null;
}

interface UpsertPackageTemplatePayload {
	packageCode: string;
	displayName: string;
	currency: CurrencyCode;
	monthlyMinimum?: string | null;
	annualCommitment?: string | null;
	metricItems: {
		metricCode: string;
		available: boolean;
		unitPrice?: string | null;
	}[];
}

interface AssignMerchantPackagePayload {
	merchantId: string;
	packageCode: string;
	idempotencyKey: string;
	reason?: string;
}

interface UpsertCatalogPricePayload {
	currency: CurrencyCode;
	billableMetrics: Record<string, string | null>;
}

interface ScopedMerchantTransactionsParams {
	currency?: CurrencyCode;
	limit?: number;
	page?: number;
	activityView?: BillingActivityView;
	dateFrom?: string;
	dateTo?: string;
	metricCodes?: string[];
	search?: string;
}

interface UsageHistoryExportParams {
	currency: CurrencyCode;
	activityView?: BillingActivityView;
	dateFrom?: string;
	dateTo?: string;
	metricCodes?: string[];
	search?: string;
}

interface MerchantAnalyticsParams {
	currency?: CurrencyCode;
	dateFrom?: string;
	dateTo?: string;
}

interface CreateMerchantTopUpPayload {
	merchantId: string;
	amount: string;
	direction: CreditDirection;
	currency: CurrencyCode;
	type: CreditTopUpType;
	details?: string;
	referenceId?: string;
	creditExpiryDate?: string | null;
}

interface ExtendMerchantGrantsExpiryPayload {
	merchantId: string;
	currency: CurrencyCode;
	creditExpiryDate: string;
}

interface UpdateMerchantGrantExpiryPayload {
	merchantId: string;
	grantId: string;
	currency: CurrencyCode;
	creditExpiryDate: string;
}

export default {
	getPeriods(): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/periods`,
			{},
			{},
			{ noCache: true }
		);
	},
	getPeriodById(id: string): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/period-reports/${id}`,
			{},
			{},
			{ noCache: true }
		);
	},
	getPeriodByIdAndMerchant(
		id: string,
		merchantId: string
	): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/period/${id}/${merchantId}`,
			{},
			{},
			{ noCache: true }
		);
	},
	getCatalog(currency: CurrencyCode): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/catalog`,
			{},
			{},
			{ params: { currency }, noCache: true }
		);
	},
	upsertCatalogPrices(
		payload: UpsertCatalogPricePayload
	): Promise<AxiosResponse> {
		return Repository.putWithAuth(`${resource}/catalog`, payload);
	},
	getMerchantOverrides(
		merchantId: string,
		currency: CurrencyCode
	): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/merchant-overrides/${merchantId}`,
			{},
			{},
			{ params: { currency }, noCache: true }
		);
	},
	getMerchantSummary(
		merchantId: string,
		currency: CurrencyCode
	): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/merchant-summary/${merchantId}`,
			{},
			{},
			{ params: { currency }, noCache: true }
		);
	},
	getMerchantTransactions(
		merchantId: string,
		currency: CurrencyCode,
		limit = 20,
		page = 1,
		activityView: BillingActivityView = 'credit_activity',
		filters: {
			dateFrom?: string;
			dateTo?: string;
			metricCodes?: string[];
			search?: string;
		} = {}
	): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/merchant-transactions/${merchantId}`,
			{},
			{},
			{
				params: {
					currency,
					limit,
					page,
					activityView,
					dateFrom: filters.dateFrom,
					dateTo: filters.dateTo,
					metricCodes: filters.metricCodes?.length
						? filters.metricCodes.join(',')
						: undefined,
					search: filters.search,
				},
				noCache: true,
			}
		);
	},
	getScopedMerchantTransactions({
		currency = 'EUR',
		limit = 10,
		page = 1,
		activityView = 'usage_history',
		dateFrom,
		dateTo,
		metricCodes,
		search,
	}: ScopedMerchantTransactionsParams = {}): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/merchant-transactions`,
			{},
			{},
			{
				params: {
					currency,
					limit,
					page,
					activityView,
					dateFrom,
					dateTo,
					metricCodes: metricCodes?.length ? metricCodes.join(',') : undefined,
					search,
				},
				noCache: true,
			}
		);
	},
	exportMerchantTransactions(
		merchantId: string,
		params: UsageHistoryExportParams
	): Promise<AxiosResponse<Blob>> {
		return Repository.getWithAuth(
			`${resource}/merchant-transactions-export/${merchantId}`,
			{},
			{},
			{
				params: {
					currency: params.currency,
					activityView: params.activityView,
					dateFrom: params.dateFrom,
					dateTo: params.dateTo,
					metricCodes: params.metricCodes?.length
						? params.metricCodes.join(',')
						: undefined,
					search: params.search,
				},
				noCache: true,
				responseType: 'blob',
			}
		);
	},
	exportScopedMerchantTransactions(
		params: UsageHistoryExportParams
	): Promise<AxiosResponse<Blob>> {
		return Repository.getWithAuth(
			`${resource}/merchant-transactions-export`,
			{},
			{},
			{
				params: {
					currency: params.currency,
					activityView: params.activityView,
					dateFrom: params.dateFrom,
					dateTo: params.dateTo,
					metricCodes: params.metricCodes?.length
						? params.metricCodes.join(',')
						: undefined,
					search: params.search,
				},
				noCache: true,
				responseType: 'blob',
			}
		);
	},
	getMerchantPricing(): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/pricing`,
			{},
			{},
			{ noCache: true }
		);
	},
	// Which modules the plan includes, without prices; open to every member where
	// getMerchantPricing is owner-only. Fetched once per session by the auth store.
	getModuleAvailability(): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/module-availability`,
			{},
			{},
			{ noCache: true }
		);
	},
	getMerchantAnalytics({
		currency = 'EUR',
		dateFrom,
		dateTo,
	}: MerchantAnalyticsParams = {}): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/merchant-analytics`,
			{},
			{},
			{
				params: { currency, dateFrom, dateTo },
				noCache: true,
			}
		);
	},
	createMerchantTopUp(
		payload: CreateMerchantTopUpPayload
	): Promise<AxiosResponse> {
		return Repository.postWithAuth(`${resource}/merchant-top-up`, payload);
	},
	extendMerchantGrantsExpiry(
		payload: ExtendMerchantGrantsExpiryPayload
	): Promise<AxiosResponse> {
		return Repository.putWithAuth(
			`${resource}/merchant-grants-expiry`,
			payload
		);
	},
	updateMerchantGrantExpiry(
		payload: UpdateMerchantGrantExpiryPayload
	): Promise<AxiosResponse> {
		return Repository.putWithAuth(`${resource}/merchant-grant-expiry`, payload);
	},

	// ── Pricing Package Templates ──────────────────────────────────────────────
	getPackageTemplates(currency: CurrencyCode): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/package-templates`,
			{},
			{},
			{ params: { currency }, noCache: true }
		);
	},
	upsertPackageTemplate(
		payload: UpsertPackageTemplatePayload
	): Promise<AxiosResponse> {
		return Repository.putWithAuth(`${resource}/package-template`, payload);
	},
	deprecatePackageTemplate(
		packageCode: string,
		currency: CurrencyCode
	): Promise<AxiosResponse> {
		return Repository.deleteWithAuth(
			`${resource}/package-template/${packageCode}?currency=${encodeURIComponent(currency)}`,
			{}
		);
	},

	// ── Merchant Package Assignment ────────────────────────────────────────────
	getMerchantPackageAssignment(
		merchantId: string,
		currency: CurrencyCode
	): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/merchant-package/${merchantId}`,
			{},
			{},
			{ params: { currency }, noCache: true }
		);
	},
	assignMerchantPackage(
		payload: AssignMerchantPackagePayload
	): Promise<AxiosResponse> {
		return Repository.postWithAuth(`${resource}/merchant-package`, payload);
	},

	requestAccountClose(): Promise<AxiosResponse> {
		return Repository.postWithAuth(`${resource}/account/close`, {});
	},

	updateCustomerDetails(
		payload: Record<string, unknown>
	): Promise<AxiosResponse> {
		return Repository.putWithAuth(
			`${resource}/account/customer-details`,
			payload
		);
	},
};
